// Ce que Radar fait en ce moment pour ce compte, visible en haut de l'écran.
//
// Radar travaillait sans le montrer : on suivait un site, l'écran annonçait
// « dès cette nuit » alors que la lecture et l'évaluation tournaient déjà, et
// les opportunités arrivaient une à une sans qu'on sache quand ce serait fini.
// Ce bandeau dit l'étape et son « X sur Y » avec son unité, une seule barre
// pour toute la tâche (issue #236), et quand une tâche se termine, il
// fait relire leurs données à tous les écrans ouverts.
//
// Interrogé toutes les deux secondes tant qu'une tâche tourne, toutes les
// quinze sinon, et IMMÉDIATEMENT quand un geste vient d'en lancer une
// (`signalerTache`) : pas de latence entre le clic et le bandeau.

import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, CircleAlert, Loader2 } from 'lucide-react'
import { get } from '../lib/api'
import { actualiserLesEcrans } from './common'
import { useT } from '../lib/i18n'
import { avancementTache, type EtapeTache } from '../lib/avancementTache'

interface Tache {
  id: number
  titre: string
  phase: 'attente' | EtapeTache | 'fini' | 'erreur'
  etapes?: EtapeTache[]
  fait: number
  total: number
  evaluees?: number
  nonClassees?: number
  message?: string
}

const TACHE_LANCEE = 'radar:tache-lancee'

/** À appeler après un geste qui lance un travail de fond (suivre un site,
 *  déposer un CV) : le bandeau interroge le serveur tout de suite. */
export function signalerTache(): void {
  window.dispatchEvent(new Event(TACHE_LANCEE))
}

const enCours = (t: Tache) => t.phase !== 'fini' && t.phase !== 'erreur'

export function BandeauTaches({ onFini }: { onFini: () => void }) {
  const t = useT()
  const [taches, setTaches] = useState<Tache[]>([])
  const actives = useRef(new Set<number>())

  const lire = useCallback(async () => {
    let lues: Tache[]
    try {
      lues = await get<Tache[]>('/api/taches')
    } catch {
      return
    }
    // Une tâche qui vient de passer de « en cours » à « finie » : tous les
    // écrans relisent leurs données, et la navigation ses compteurs.
    const finies = lues.filter((x) => !enCours(x) && actives.current.has(x.id))
    actives.current = new Set(lues.filter(enCours).map((x) => x.id))
    setTaches(lues)
    if (finies.length > 0) {
      actualiserLesEcrans()
      onFini()
    }
  }, [onFini])

  const quelqueChoseTourne = taches.some(enCours)
  useEffect(() => {
    void lire()
    const minuterie = setInterval(() => void lire(), quelqueChoseTourne ? 2000 : 15000)
    const surLancement = () => void lire()
    window.addEventListener(TACHE_LANCEE, surLancement)
    return () => {
      clearInterval(minuterie)
      window.removeEventListener(TACHE_LANCEE, surLancement)
    }
  }, [lire, quelqueChoseTourne])

  if (taches.length === 0) return null
  return (
    <div className="bandeau-taches" role="status" aria-live="polite">
      {taches.map((x) => (
        <div key={x.id} className={`tache tache-${x.phase}`}>
          {x.phase === 'fini' ? <Check size={15} /> : x.phase === 'erreur' ? <CircleAlert size={15} /> : <Loader2 size={15} className="tourne" />}
          <b>{x.titre || t('taches.cv')}</b>
          <span>
            {x.phase === 'fini'
              ? x.nonClassees
                ? t('taches.finiEnAttente', { n: x.evaluees ?? 0, m: x.nonClassees })
                : t('taches.fini', { n: x.evaluees ?? 0 })
              : x.phase === 'erreur'
                ? t('taches.erreur', { message: x.message ?? '' })
                : x.total > 0 && x.phase !== 'attente'
                  ? t(`taches.unite.${x.phase}`, { fait: x.fait, total: x.total })
                  : t(`taches.phase.${x.phase}`)}
          </span>
          {enCours(x) && x.phase !== 'attente' && (
            <span className="tache-barre" aria-hidden="true">
              <span style={{ width: `${Math.round(100 * avancementTache(x))}%` }} />
            </span>
          )}
        </div>
      ))}
    </div>
  )
}
